import { convertSaveFieldValuesToFieldValueResponses } from '@monorepo/logs/src/components/LogEntriesReviewModal/convertSaveFieldValuesToFieldValueResponses';
import { SelectOptionsByFieldType } from '@monorepo/shared/apiClient/logs/fieldOptionsForUpload';
import { getFieldValueFromForm } from '@monorepo/shared/componentsV2/fieldDataType/values/EditFieldValueFactory/EditFieldValueFactory';
import { FormValues } from '@monorepo/shared/componentsV2/fieldDataType/values/EditFieldValueFactory/types';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import {
  DateLike,
  FieldResponse,
  SaveLogEntryRequest,
  UTCEquivalentOfLocal,
} from 'mapistry-shared';
import { isLogEntryComplete } from './utils/logEntryStatus';

export type LogEntriesFormValues = { [logEntryId: string]: FormValues };

export const convertLogEntriesFormFieldValuesToSaveLogEntryRequests = ({
  logEntries,
  formValues,
  logFields,
}: {
  logEntries: LogEntry[];
  formValues: Partial<LogEntriesFormValues>;
  logFields: FieldResponse[];
}): Record<string, SaveLogEntryRequest> =>
  logEntries.reduce((acc, entry) => {
    const entryFormFieldValues = formValues[entry.id];
    if (!entryFormFieldValues) return acc;

    // Iterate over ALL log fields (not just form values) to ensure fields
    // without values are preserved as null instead of being dropped entirely.
    const logFieldValues = logFields.map((logField) =>
      getFieldValueFromForm(logField, entryFormFieldValues),
    );

    return {
      ...acc,
      [entry.id]: {
        id: entry.id,
        logDate: UTCEquivalentOfLocal(entryFormFieldValues.logDate as DateLike),
        logFieldValues,
        isReviewed: !!entry.reviewedBy,
      },
    };
    // TODO(todo@edpAi): Don't rely on LogEntry['id'] for generated entries (they should not have an ID)
  }, {} as Record<LogEntry['id'], SaveLogEntryRequest>);

export const convertLogEntriesFormFieldValuesToLogEntryResponses = ({
  logId,
  projectId,
  logEntries,
  formValues,
  logFields,
  logFieldOptions,
}: {
  logId: string;
  projectId: string;
  logEntries: LogEntry[];
  formValues: Partial<LogEntriesFormValues>;
  logFields: FieldResponse[];
  logFieldOptions: SelectOptionsByFieldType;
  // TODO(todo@edpAi): Don't rely on LogEntry['id'] for generated entries (they should not have an ID)
}): Record<LogEntry['id'], LogEntry> => {
  const saveLogEntryRequests =
    convertLogEntriesFormFieldValuesToSaveLogEntryRequests({
      logEntries,
      formValues,
      logFields,
    });
  const reviewerByEntryId = new Map(
    logEntries.map((entry) => [entry.id, entry.reviewedBy]),
  );

  return logEntries.reduce(
    (acc, existingEntry) => {
      const { id } = existingEntry;
      const entry = saveLogEntryRequests[id];
      if (!entry) return acc;

      return {
        ...acc,
        [id]: {
          ...existingEntry,
          logId,
          projectId,
          isComplete: isLogEntryComplete({
            entryId: id,
            logEntry: existingEntry,
            errors: undefined,
            formValues,
            logFields,
          }),
          logDate: entry.logDate,
          reviewedBy: reviewerByEntryId.get(id) ?? null,
          logFieldValues: convertSaveFieldValuesToFieldValueResponses({
            logFields,
            logFieldOptions,
            entry,
          }),
        },
      };
    },
    // TODO(todo@edpAi): Don't rely on LogEntry['id'] for generated entries (they should not have an ID)
    {} as Record<LogEntry['id'], LogEntry>,
  );
};
